import { useState, useRef } from 'react';
import { Send } from 'lucide-react';
import { motion } from 'framer-motion';
import FloatingLabelInput from '../ui/FloatingLabelInput';
import PremiumButton from '../ui/PremiumButton';
import { submitContact } from '../../lib/contact';
import { useProductSelection } from '../../contexts/ProductSelectionContext';
import { trackFormSubmission } from '../../lib/analytics';

interface ContactFormProps {
  onSubmitSuccess?: () => void;
}

export default function ContactForm({ onSubmitSuccess }: ContactFormProps = {}) {
  const { selectedProduct, selectedAddOns, getTotal, clearSelection } = useProductSelection();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    business: '',
    service: selectedProduct?.name.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'dean',
    message: ''
  });

  const submittingRef = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-populate message with selections
  useState(() => {
    if (selectedProduct || selectedAddOns.length > 0) {
      let autoMessage = "I'm interested in the following configuration:\n\n";
      
      if (selectedProduct) {
        autoMessage += `AI Assistant: ${selectedProduct.name}\n`;
        autoMessage += `Setup Price: ${selectedProduct.setupPrice}\n`;
        if (selectedProduct.recurringPrice) {
          autoMessage += `Monthly: ${selectedProduct.recurringPrice} ${selectedProduct.billingCycle}\n`;
        }
      }
      
      if (selectedAddOns.length > 0) {
        autoMessage += `\nSelected Add-ons:\n`;
        selectedAddOns.forEach(addon => {
          autoMessage += `• ${addon.name} - ${addon.price}\n`;
        });
      }
      
      autoMessage += `\nTotal Setup Investment: ${getTotal()}\n\n`;
      autoMessage += "I'd like to schedule a strategy call to discuss if this is a good fit for my business.";
      
      setFormData(prev => ({ ...prev, message: autoMessage }));
    }
  });
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setError(null);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const utmCampaign = urlParams.get('utm_campaign');

      await submitContact({ ...formData, utmSource, utmMedium, utmCampaign });

      trackFormSubmission('contact_form', true);
      setSubmitted(true);
      onSubmitSuccess?.();
      clearSelection(); // Clear selections after successful submission
      setFormData({
        name: '',
        email: '',
        business: '',
        service: 'dean',
        message: ''
      });
    } catch (err) {
      console.error('Form submission error:', err);
      trackFormSubmission('contact_form', false);
      setError('There was an issue submitting your form. Please try again or contact us directly.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (submitted) return (
    <div role="status" className="rounded-xl border border-green-200 bg-green-50 p-6 text-green-900">
      <p className="font-semibold">Your message has been received.</p>
      <p className="mt-2">We'll get back to you within 24 hours.</p>
      <button type="button" onClick={() => setSubmitted(false)} className="mt-4 underline min-h-[44px]">Send another message</button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <FloatingLabelInput
        label="Your Name *"
        type="text"
        id="name"
        autoComplete="name"
        maxLength={80}
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="min-h-[56px] text-base"
        required
      />

      <FloatingLabelInput
        label="Email Address *"
        type="email"
        id="email"
        autoComplete="email"
        inputMode="email"
        maxLength={254}
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        className="min-h-[56px] text-base"
        required
      />

      <FloatingLabelInput
        label="Business Name"
        type="text"
        id="business"
        autoComplete="organization"
        maxLength={120}
        value={formData.business}
        onChange={(e) => setFormData({ ...formData, business: e.target.value })}
        className="min-h-[56px] text-base"
      />

      <div className="relative">
        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
          Which AI Assistant Interests You Most?
        </label>
        <select
          id="service"
          value={formData.service}
          onChange={(e) => setFormData({ ...formData, service: e.target.value })}
          className="w-full bg-gray-50 border-2 border-gray-200 rounded-lg px-4 py-4 text-gray-900 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 transition-all duration-300 font-medium min-h-[56px] text-base "
        >
          <option value="dean">Speed-to-lead caller</option>
          <option value="ralph">Lead-list reactivation</option>
          <option value="sara">24/7 Receptionist</option>
          <option value="custom-pbs">Something Special</option>
          <option value="other">Not sure yet - let's discuss</option>
        </select>
      </div>

      <div>
        <FloatingLabelInput
          label="Tell Us About Your Business *"
          type="textarea"
          id="message"
          maxLength={3000}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          rows={4}
          className="text-base"
          required
        />
        <p className="text-xs sm:text-sm text-gray-500 mt-2">This helps us prepare a customized demo for your business</p>
      </div>

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          role="alert"
          className="text-red-600 text-sm font-medium bg-red-50 border-2 border-red-200 rounded-lg px-4 py-4"
        >
          <strong>Oops!</strong> {error} Please try again or email us directly at noah@pointbreak.systems
        </motion.div>
      )}

      <PremiumButton
        type="submit"
        variant="primary"
        isLoading={isSubmitting}
        disabled={isSubmitting}
        className="w-full min-h-[56px]"
      >
        <Send className="w-5 h-5" />
        <span className="text-base sm:text-lg">
          {isSubmitting ? 'Sending Your Request...' : 'Get My Custom Demo'}
        </span>
      </PremiumButton>
    </form>
  );
}
